
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { all_routes } from "../router/all_routes";
import { toast } from "react-toastify";
import AOS from "aos";

// Import the JSON data
import carDataRaw from "../admin/makes_models.json";

// Define Types for the JSON structure
interface CarModelMap {
  [modelName: string]: string; // "ModelName": "ID"
}

interface CarMakeData {
  id: string;
  models: CarModelMap;
}

interface CarDataMap {
  [makeName: string]: CarMakeData;
}

const carData = carDataRaw as CarDataMap;

// Define the interface for the form data
interface CarRequestFormData {
    make: string;
    model: string;
    minYear: string;
    maxYear: string;
    minBudget: string;
    maxBudget: string;
    fuelType: string;
    transmission: string;
    description: string;
    contactName: string;
    contactPhone: string;
    contactEmail: string;
}

const CarRequest = () => {
    const navigate = useNavigate();
    const routes = all_routes;
    
    // Initial state
    const initialFormData: CarRequestFormData = {
        make: "",
        model: "",
        minYear: "",
        maxYear: "",
        minBudget: "",
        maxBudget: "",
        fuelType: "",
        transmission: "",
        description: "",
        contactName: "",
        contactPhone: "",
        contactEmail: ""
    };

    const [formData, setFormData] = useState<CarRequestFormData>(initialFormData);
    const [loading, setLoading] = useState(false);
    
    // Derived state for models based on selected make
    const availableModels = formData.make && carData[formData.make] 
        ? Object.keys(carData[formData.make].models).sort() 
        : [];

    useEffect(() => {
        AOS.init({ duration: 1200, once: true });
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        
        // Special handling for Make change to reset Model
        if (name === "make") {
             setFormData(prev => ({
                ...prev,
                make: value,
                model: "" // Reset model when make changes
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Convert string numbers to actual numbers for the API
            const payload = {
                ...formData,
                minYear: formData.minYear ? parseInt(formData.minYear) : null,
                maxYear: formData.maxYear ? parseInt(formData.maxYear) : null,
                minBudget: formData.minBudget ? parseFloat(formData.minBudget) : null,
                maxBudget: formData.maxBudget ? parseFloat(formData.maxBudget) : null,
            };

            await axios.post("/api/v1/car-request", payload);
            toast.success("Cererea a fost trimisă cu succes! Te vom contacta în curând.");
            // Reset form
            setFormData(initialFormData);
        } catch (error) {
            console.error("Error submitting request:", error);
            toast.error("A apărut o eroare la trimiterea cererii.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="main-wrapper">
             {/* Header Background */}
             <div className="breadcrumb-bar" style={{ padding: '80px 0 40px' }}>
                <div className="container">
                    <div className="row align-items-center text-center">
                        <div className="col-md-12 col-12">
                            <h2 className="breadcrumb-title">Comandă Mașina Dorită</h2>
                            <nav aria-label="breadcrumb" className="page-breadcrumb">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to={routes.home}>Acasă</Link>
                                    </li>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        Car Request
                                    </li>
                                </ol>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>

            <section className="section-modern" style={{ backgroundColor: '#f9f9f9', padding: '60px 0' }}>
                <div className="container">
                     <div className="section-heading-modern text-center mb-5" data-aos="fade-up">
                        <div className="section-badge">
                          <i className="fa-solid fa-star"></i>
                          <span>Serviciu Premium</span>
                        </div>
                        <h2>Spune-ne ce mașină cauți</h2>
                        <p>
                          Noi o vom importa pentru tine de la cele mai bune licitații europene.
                          <br />Expertiză, transparență și livrare direct la tine acasă.
                        </p>
                      </div>

                    <div className="row justify-content-center">
                        <div className="col-lg-10 col-xl-9">
                            <div 
                                className="contact-form-box" 
                                style={{ 
                                    background: '#fff', 
                                    padding: '40px', 
                                    borderRadius: '15px', 
                                    boxShadow: '0 10px 30px rgba(0,0,0,0.05)' 
                                }}
                                data-aos="fade-up" 
                                data-aos-delay="200"
                            >
                                <form onSubmit={handleSubmit}>
                                    <h4 className="card-title mb-4" style={{ borderBottom: '2px solid #f0f0f0', paddingBottom: '15px', color: '#333' }}>
                                        <i className="fa-solid fa-car me-2" style={{ color: '#FFC107' }}></i>
                                        Detalii Autoturism
                                    </h4>
                                    <div className="row">
                                        {/* Car Details */}
                                        <div className="col-lg-6 col-md-6 col-12">
                                            <div className="input-block mb-3">
                                                <label style={{ fontWeight: 600, color: '#555', marginBottom: '8px' }}>Marcă <span className="text-danger">*</span></label>
                                                <select 
                                                    className="form-control" 
                                                    name="make"
                                                    value={formData.make}
                                                    onChange={handleChange}
                                                    required
                                                    style={{ height: '48px', borderRadius: '8px' }}
                                                >
                                                    <option value="">Selectează Marca</option>
                                                    {/* Popular Makes Section */}
                                                    <optgroup label="Mărci Populare">
                                                        {["Audi", "BMW", "Mercedes-Benz", "Volkswagen"].map(make => (
                                                            <option key={make} value={make}>{make}</option>
                                                        ))}
                                                    </optgroup>
                                                    
                                                    {/* All Makes Section */}
                                                    <optgroup label="Toate Mărcile">
                                                        {Object.keys(carData)
                                                            .filter(make => !["Audi", "BMW", "Mercedes-Benz", "Volkswagen"].includes(make))
                                                            .sort()
                                                            .map(make => (
                                                                <option key={make} value={make}>{make}</option>
                                                        ))}
                                                    </optgroup>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-lg-6 col-md-6 col-12">
                                            <div className="input-block mb-3">
                                                <label style={{ fontWeight: 600, color: '#555', marginBottom: '8px' }}>Model <span className="text-danger">*</span></label>
                                                 <select 
                                                    className="form-control" 
                                                    name="model"
                                                    value={formData.model}
                                                    onChange={handleChange}
                                                    required
                                                    disabled={!formData.make}
                                                    style={{ height: '48px', borderRadius: '8px' }}
                                                >
                                                    <option value="">Selectează Modelul</option>
                                                    {availableModels.map(model => (
                                                        <option key={model} value={model}>{model}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        
                                        {/* Year Range */}
                                        <div className="col-lg-6 col-md-6 col-12">
                                            <div className="input-block mb-3">
                                                <label style={{ fontWeight: 600, color: '#555', marginBottom: '8px' }}>An Minim</label>
                                                <input 
                                                    type="number" 
                                                    className="form-control" 
                                                    placeholder="Ex: 2018"
                                                    name="minYear"
                                                    value={formData.minYear}
                                                    onChange={handleChange}
                                                    style={{ height: '48px', borderRadius: '8px' }}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-lg-6 col-md-6 col-12">
                                            <div className="input-block mb-3">
                                                <label style={{ fontWeight: 600, color: '#555', marginBottom: '8px' }}>An Maxim</label>
                                                <input 
                                                    type="number" 
                                                    className="form-control" 
                                                    placeholder="Ex: 2023"
                                                    name="maxYear"
                                                    value={formData.maxYear}
                                                    onChange={handleChange}
                                                    style={{ height: '48px', borderRadius: '8px' }}
                                                />
                                            </div>
                                        </div>

                                        {/* Budget Range */}
                                        <div className="col-lg-6 col-md-6 col-12">
                                            <div className="input-block mb-3">
                                                <label style={{ fontWeight: 600, color: '#555', marginBottom: '8px' }}>Buget Minim (€)</label>
                                                <input 
                                                    type="number" 
                                                    className="form-control" 
                                                    placeholder="Ex: 15000"
                                                    name="minBudget"
                                                    value={formData.minBudget}
                                                    onChange={handleChange}
                                                    style={{ height: '48px', borderRadius: '8px' }}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-lg-6 col-md-6 col-12">
                                            <div className="input-block mb-3">
                                                <label style={{ fontWeight: 600, color: '#555', marginBottom: '8px' }}>Buget Maxim (€)</label>
                                                <input 
                                                    type="number" 
                                                    className="form-control" 
                                                    placeholder="Ex: 25000"
                                                    name="maxBudget"
                                                    value={formData.maxBudget}
                                                    onChange={handleChange}
                                                    style={{ height: '48px', borderRadius: '8px' }}
                                                />
                                            </div>
                                        </div>

                                        {/* Fuel & Transmission */}
                                        <div className="col-lg-6 col-md-6 col-12">
                                            <div className="input-block mb-3">
                                                <label style={{ fontWeight: 600, color: '#555', marginBottom: '8px' }}>Combustibil</label>
                                                <select 
                                                    className="form-control" 
                                                    name="fuelType"
                                                    value={formData.fuelType}
                                                    onChange={handleChange}
                                                    style={{ height: '48px', borderRadius: '8px' }}
                                                >
                                                    <option value="">Selectează Combustibil</option>
                                                    <option value="PETROL">Benzină</option>
                                                    <option value="DIESEL">Diesel</option>
                                                    <option value="HYBRID">Hibrid</option>
                                                    <option value="ELECTRIC">Electric</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-lg-6 col-md-6 col-12">
                                            <div className="input-block mb-3">
                                                <label style={{ fontWeight: 600, color: '#555', marginBottom: '8px' }}>Transmisie</label>
                                                <select 
                                                    className="form-control" 
                                                    name="transmission"
                                                    value={formData.transmission}
                                                    onChange={handleChange}
                                                    style={{ height: '48px', borderRadius: '8px' }}
                                                >
                                                    <option value="">Selectează Transmisie</option>
                                                    <option value="MANUAL">Manuală</option>
                                                    <option value="AUTOMATIC">Automată</option>
                                                </select>
                                            </div>
                                        </div>

                                        {/* Description */}
                                        <div className="col-lg-12">
                                            <div className="input-block mb-4">
                                                <label style={{ fontWeight: 600, color: '#555', marginBottom: '8px' }}>Alte Detalii / Preferințe</label>
                                                <textarea 
                                                    className="form-control" 
                                                    rows={4} 
                                                    placeholder="Ex: Culoare neagră, pachet sport, trapă panoramică..."
                                                    name="description"
                                                    value={formData.description}
                                                    onChange={handleChange}
                                                    style={{ borderRadius: '8px' }}
                                                ></textarea>
                                            </div>
                                        </div>

                                        {/* Contact Details */}
                                        <div className="col-lg-12">
                                            <h4 className="mt-4 mb-4" style={{ borderBottom: '2px solid #f0f0f0', paddingBottom: '15px', color: '#333' }}>
                                                <i className="fa-regular fa-id-card me-2" style={{ color: '#FFC107' }}></i>
                                                Informații Contact
                                            </h4>
                                        </div>
                                        
                                        <div className="col-lg-4 col-md-6 col-12">
                                            <div className="input-block mb-3">
                                                <label style={{ fontWeight: 600, color: '#555', marginBottom: '8px' }}>Nume <span className="text-danger">*</span></label>
                                                <input 
                                                    type="text" 
                                                    className="form-control" 
                                                    placeholder="Numele tău complet"
                                                    name="contactName"
                                                    value={formData.contactName}
                                                    onChange={handleChange}
                                                    required
                                                    style={{ height: '48px', borderRadius: '8px' }}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-lg-4 col-md-6 col-12">
                                            <div className="input-block mb-3">
                                                <label style={{ fontWeight: 600, color: '#555', marginBottom: '8px' }}>Telefon <span className="text-danger">*</span></label>
                                                <input 
                                                    type="text" 
                                                    className="form-control" 
                                                    placeholder="Număr de telefon"
                                                    name="contactPhone"
                                                    value={formData.contactPhone}
                                                    onChange={handleChange}
                                                    required
                                                    style={{ height: '48px', borderRadius: '8px' }}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-lg-4 col-md-6 col-12">
                                            <div className="input-block mb-3">
                                                <label style={{ fontWeight: 600, color: '#555', marginBottom: '8px' }}>Email</label>
                                                <input 
                                                    type="email" 
                                                    className="form-control" 
                                                    placeholder="Adresa de email"
                                                    name="contactEmail"
                                                    value={formData.contactEmail}
                                                    onChange={handleChange}
                                                    style={{ height: '48px', borderRadius: '8px' }}
                                                />
                                            </div>
                                        </div>

                                        {/* Submit Button */}
                                        <div className="col-lg-12 text-center mt-4">
                                            <button 
                                                className="btn btn-primary submit-btn px-5 py-3" 
                                                type="submit"
                                                disabled={loading}
                                                style={{ 
                                                    fontSize: '1.1rem', 
                                                    fontWeight: 600, 
                                                    borderRadius: '30px',
                                                    boxShadow: '0 4px 15px rgba(255, 193, 7, 0.4)',
                                                    transition: 'all 0.3s ease'
                                                }}
                                            >
                                                {loading ? (
                                                    <span><i className="fas fa-spinner fa-spin me-2"></i> Se trimite...</span>
                                                ) : (
                                                    <span><i className="feather icon-send me-2"></i> Trimite Cererea</span>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CarRequest;
